import { BrickSizes, JointClass, JOINTS, JointSize, StructureType } from "../constants";
import { LAYOUT_VERSION } from "../wall/layout";

// Everything needed to rebuild a wall configuration and its layout. The share
// link carries all of it, so nothing has to be stored to resolve a link.
export interface SharedWall {
  selectedBricks: Record<string, number>;
  brickSize: BrickSizes;
  structure: StructureType;
  joint: JointClass;
  horizontalJointSize: JointSize;
  verticalJointSize: JointSize;
  scale: number;
  seed: number;
}

// The wall travels as one base64url parameter holding these bytes:
//
//   format, layout version, brick size, structure, horizontal joint size,
//   vertical joint size, scale (percent), seed (uint32),
//   joint name length + UTF-8 name,
//   brick series count + per series its 12-byte ObjectId and its rate
//
// Bump FORMAT_VERSION on any change to that order; links in another format
// are ignored. The layout version lets a later layout algorithm recognise
// (and reproduce or flag) walls shared before it changed. See ADR 0001.
const PARAM = "w";
const FORMAT_VERSION = 1;

// The codes below are part of the link format: only ever append to these lists.
const BRICK_SIZES = [
  BrickSizes.M50,
  BrickSizes.M65,
  BrickSizes.HF,
  BrickSizes.WF,
  BrickSizes.DL,
  BrickSizes.DF40,
  BrickSizes.DF52,
  BrickSizes.LF36540,
  BrickSizes.LF36552,
  BrickSizes.LF490,
];
const STRUCTURES = [
  StructureType.HalfBrick,
  StructureType.Wild,
  StructureType.Stack,
  StructureType.Cross,
];
const JOINT_SIZES = [JointSize.Glued, JointSize.Small, JointSize.Large];

const MIN_SCALE = 0.25;
const MAX_SCALE = 1.5;
const OBJECT_ID = /^[0-9a-f]{24}$/;

// A rate is a uint16: up to 10000 hundredths of a percent, or 100/k (what
// adding a series produces), or a marker followed by the float64 itself. Rates
// must stay exact: the layout compares them against the seeded hash, so a
// rounded rate could swap a brick near a boundary.
const MAX_HUNDREDTHS = 10000;
const RATE_FRACTION = 0xff00;
const RATE_FLOAT = 0xffff;

class Writer {
  private bytes: number[] = [];
  private scratch = new DataView(new ArrayBuffer(8));

  u8(value: number) {
    this.bytes.push(value & 0xff);
  }

  u16(value: number) {
    this.scratch.setUint16(0, value);
    this.copy(2);
  }

  u32(value: number) {
    this.scratch.setUint32(0, value);
    this.copy(4);
  }

  f64(value: number) {
    this.scratch.setFloat64(0, value);
    this.copy(8);
  }

  raw(bytes: Uint8Array) {
    this.bytes.push(...bytes);
  }

  private copy(length: number) {
    for (let i = 0; i < length; i++) this.bytes.push(this.scratch.getUint8(i));
  }

  toBase64Url() {
    return btoa(String.fromCharCode(...this.bytes))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }
}

// Every read past the end throws a RangeError, which rejects the whole link.
class Reader {
  private view: DataView;
  private offset = 0;

  constructor(base64Url: string) {
    const binary = atob(base64Url.replace(/-/g, "+").replace(/_/g, "/"));
    this.view = new DataView(Uint8Array.from(binary, (c) => c.charCodeAt(0)).buffer);
  }

  u8() {
    return this.view.getUint8(this.offset++);
  }

  u16() {
    const value = this.view.getUint16(this.offset);
    this.offset += 2;
    return value;
  }

  u32() {
    const value = this.view.getUint32(this.offset);
    this.offset += 4;
    return value;
  }

  f64() {
    const value = this.view.getFloat64(this.offset);
    this.offset += 8;
    return value;
  }

  raw(length: number) {
    if (this.offset + length > this.view.byteLength) throw new RangeError("Link is cut off");
    const bytes = new Uint8Array(this.view.buffer, this.offset, length);
    this.offset += length;
    return bytes;
  }
}

const writeRate = (w: Writer, rate: number) => {
  const hundredths = Math.round(rate * 100);
  if (hundredths / 100 === rate) return w.u16(hundredths);
  const k = Math.round(100 / rate);
  if (k > 0 && k < RATE_FLOAT - RATE_FRACTION && 100 / k === rate) return w.u16(RATE_FRACTION + k);
  w.u16(RATE_FLOAT);
  w.f64(rate);
};

const readRate = (r: Reader) => {
  const code = r.u16();
  if (code <= MAX_HUNDREDTHS) return code / 100;
  if (code > RATE_FRACTION && code < RATE_FLOAT) return 100 / (code - RATE_FRACTION);
  if (code === RATE_FLOAT) return r.f64();
  return NaN;
};

const hexToBytes = (hex: string) =>
  Uint8Array.from(hex.match(/../g)!, (byte) => parseInt(byte, 16));

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

// Writes the wall as the value of the share link's one parameter. Brick series
// ids are always MongoDB ObjectIds; anything else cannot be in a link.
export const writeWallLink = (wall: SharedWall): string => {
  const w = new Writer();
  w.u8(FORMAT_VERSION);
  w.u8(LAYOUT_VERSION);
  w.u8(BRICK_SIZES.indexOf(wall.brickSize));
  w.u8(STRUCTURES.indexOf(wall.structure));
  w.u8(JOINT_SIZES.indexOf(wall.horizontalJointSize));
  w.u8(JOINT_SIZES.indexOf(wall.verticalJointSize));
  w.u8(Math.round(wall.scale * 100));
  w.u32(wall.seed);

  const joint = new TextEncoder().encode(wall.joint.name);
  w.u8(joint.length);
  w.raw(joint);

  const bricks = Object.entries(wall.selectedBricks)
    .filter(([id]) => OBJECT_ID.test(id))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  w.u8(bricks.length);
  for (const [id, rate] of bricks) {
    w.raw(hexToBytes(id));
    writeRate(w, rate);
  }
  return w.toBase64Url();
};

// Reads a share link. Returns null when the URL carries no wall, or one that
// is damaged or in another format. A setting this version doesn't know (from
// a newer widget, say) is left out, so the rest of the wall still restores.
export const readWallLink = (search: string): Partial<SharedWall> | null => {
  const value = new URLSearchParams(search).get(PARAM);
  if (!value) return null;
  try {
    const r = new Reader(value);
    if (r.u8() !== FORMAT_VERSION) return null;
    r.u8(); // Layout version: only one exists so far.

    const wall: Partial<SharedWall> = {};
    const brickSize = BRICK_SIZES[r.u8()];
    if (brickSize) wall.brickSize = brickSize;
    const structure = STRUCTURES[r.u8()];
    if (structure) wall.structure = structure;
    const horizontal = JOINT_SIZES[r.u8()];
    if (horizontal) wall.horizontalJointSize = horizontal;
    const vertical = JOINT_SIZES[r.u8()];
    if (vertical) wall.verticalJointSize = vertical;
    const scale = r.u8() / 100;
    if (scale >= MIN_SCALE && scale <= MAX_SCALE) wall.scale = scale;
    wall.seed = r.u32();

    const jointName = new TextDecoder().decode(r.raw(r.u8()));
    const joint = JOINTS.find((j) => j.name === jointName);
    if (joint) wall.joint = joint;

    const selectedBricks: Record<string, number> = {};
    const count = r.u8();
    for (let i = 0; i < count; i++) {
      const id = bytesToHex(r.raw(12));
      const rate = readRate(r);
      if (!(rate >= 0 && rate <= 100)) return null;
      selectedBricks[id] = rate;
    }
    if (count === 0) return null;
    wall.selectedBricks = selectedBricks;
    return wall;
  } catch {
    return null;
  }
};

// The given URL with its wall replaced by this one. Query parameters that are
// not part of the wall (from an embedding page, say) are left alone. An empty
// brick selection has nothing to share, so it removes the wall from the URL.
export const withWallLink = (href: string, wall: SharedWall): string => {
  const url = new URL(href);
  url.searchParams.delete(PARAM);
  if (Object.keys(wall.selectedBricks).length > 0) {
    url.searchParams.set(PARAM, writeWallLink(wall));
  }
  return url.toString();
};
